import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useRef, useState } from 'react'
import baseUrl from '../../api/baseUrl'
import LabQuickAddPatientModal from './LabQuickAddPatientModal'
import {
  type ClinicPatientOption,
  formatPatientLabel,
  formatPatientMeta,
} from './labPatientUtils'

type LabPatientPickerProps = {
  patientLabel: string
  patientMeta: string
  onSelect: (patient: string, patientMeta: string) => void
  onClear?: () => void
  showError?: boolean
  /** Show selected patient only — no Change / search / add. */
  readOnly?: boolean
}

const SEARCH_DEBOUNCE_MS = 280

export default function LabPatientPicker({
  patientLabel,
  patientMeta,
  onSelect,
  onClear,
  showError = false,
  readOnly = false,
}: LabPatientPickerProps) {
  const [searchInput, setSearchInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<ClinicPatientOption[]>([])
  const [loading, setLoading] = useState(false)
  const [listOpen, setListOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasSelection = Boolean(patientLabel.trim())

  useEffect(() => {
    const timer = window.setTimeout(() => setKeyword(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const loadPatients = useCallback((q: string) => {
    setLoading(true)
    baseUrl
      .get('client/patient/find-all-patients', {
        headers: {
          status: true,
          page: 1,
          keyword: q,
          searchBy: 'all',
          limit: 12,
        },
      })
      .then((res) => setResults(res.data.data ?? []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!listOpen) return
    loadPatients(keyword)
  }, [keyword, listOpen, loadPatients])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setListOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  function pickPatient(p: ClinicPatientOption) {
    setSelectedId(p._id)
    setListOpen(false)
    setSearchInput('')
    onSelect(formatPatientLabel(p), formatPatientMeta(p))
  }

  function clearSelection() {
    setSelectedId(null)
    setSearchInput('')
    setListOpen(true)
    onClear?.()
    onSelect('', '')
    window.setTimeout(() => inputRef.current?.focus(), 0)
  }

  function openSearch() {
    setListOpen(true)
    loadPatients(searchInput.trim())
  }

  return (
    <div ref={containerRef} className="space-y-3">
      {hasSelection ? (
        <div
          className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
            showError
              ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
              : 'border-primary/30 bg-primary/[0.04] dark:border-primary/40 dark:bg-primary/10'
          }`}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-base font-semibold text-white shadow-sm">
            {patientLabel.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {patientLabel}
              </p>
              <CheckCircleIcon
                className="shrink-0 text-primary"
                sx={{ fontSize: 16 }}
              />
            </div>
            {patientMeta ? (
              <p className="mt-0.5 truncate text-xs text-slate-500">{patientMeta}</p>
            ) : null}
          </div>
          {readOnly ? null : (
            <button
              type="button"
              onClick={clearSelection}
              className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
            >
              Change
            </button>
          )}
        </div>
      ) : readOnly ? (
        <p
          className={`rounded-xl border px-4 py-3 text-sm ${
            showError
              ? 'border-red-300 bg-red-50/50 text-red-700 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300'
              : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-600 dark:bg-slate-800/50'
          }`}
        >
          {showError ? 'Patient is required for this treatment.' : 'No patient on this treatment.'}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                sx={{ fontSize: 20 }}
              />
              <input
                ref={inputRef}
                type="search"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  setListOpen(true)
                }}
                onFocus={openSearch}
                placeholder="Search patient name, reg no, or phone…"
                aria-invalid={showError}
                className={`h-11 w-full rounded-xl border bg-white pl-10 pr-3 text-sm text-slate-900 shadow-sm transition-colors focus:outline-none focus:ring-2 dark:bg-slate-900 dark:text-slate-100 ${
                  showError
                    ? 'border-red-400 focus:border-red-400 focus:ring-red-200 dark:border-red-700 dark:focus:ring-red-900/40'
                    : 'border-slate-300 focus:border-primary focus:ring-primary/20 dark:border-slate-600'
                }`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && results.length > 0) {
                    e.preventDefault()
                    pickPatient(results[0])
                  }
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => setQuickAddOpen(true)}
              className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
            >
              <PersonAddIcon sx={{ fontSize: 18 }} />
              Add new
            </button>
          </div>

          {showError ? (
            <p className="text-xs font-medium text-red-600 dark:text-red-400">
              Select an existing patient or add a new one
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              Search your clinic list or add a new patient without leaving this
              page.
            </p>
          )}

          {listOpen ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md dark:border-slate-700 dark:bg-slate-800">
              {loading ? (
                <div className="flex items-center justify-center gap-2 px-4 py-6 text-xs text-slate-500">
                  <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Loading patients…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <PersonIcon
                    sx={{ fontSize: 28 }}
                    className="mx-auto mb-2 text-slate-300"
                  />
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                    {keyword ? 'No patients found' : 'No patients yet'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {keyword
                      ? 'Try a different search or add as new'
                      : 'Add your first patient below'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setQuickAddOpen(true)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight px-4 py-2 text-sm font-semibold text-white shadow-sm"
                  >
                    <PersonAddIcon sx={{ fontSize: 18 }} />
                    Add new patient
                  </button>
                </div>
              ) : (
                <>
                  <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:border-slate-700">
                    {keyword ? `${results.length} result(s)` : 'Recent patients'} — click to
                    select
                  </p>
                  <ul className="max-h-52 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-700">
                    {results.map((p) => (
                      <li key={p._id}>
                        <button
                          type="button"
                          onClick={() => pickPatient(p)}
                          className={`flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50 ${
                            selectedId === p._id ? 'bg-primary/10' : ''
                          }`}
                        >
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            {(p.fullName || '?').charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                              {p.fullName || 'Patient'}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-slate-500">
                              {[p.regNo, p.mobileNo].filter(Boolean).join(' · ') ||
                                'No reg / phone'}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : null}
        </>
      )}

      <LabQuickAddPatientModal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        initialName={searchInput.trim()}
        onCreated={(patient) => pickPatient(patient)}
      />
    </div>
  )
}
